import React from 'react';
import { Box, Container, Typography, Tabs, Tab, Stack, Link } from '@mui/material';
import { DocumentScanner, Security, Gavel, Assignment } from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`legal-tabpanel-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

export const LegalPage: React.FC = () => {
  const [value, setValue] = React.useState(0);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        <Typography variant="h3" align="center" gutterBottom>
          Legal Information
        </Typography>
        <Typography 
          variant="subtitle1" 
          align="center" 
          color="text.secondary"
          sx={{ mb: 6 }}
        >
          Our commitment to transparency and legal compliance
        </Typography>

        <Tabs
          value={value}
          onChange={handleChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Gavel />} label="Terms of Service" />
          <Tab icon={<Security />} label="Privacy Policy" />
          <Tab icon={<Assignment />} label="Acceptable Use" />
          <Tab icon={<DocumentScanner />} label="License Agreement" />
        </Tabs>

        <TabPanel value={value} index={0}>
          <Stack spacing={4}>
            <Box>
              <Typography variant="h5" gutterBottom>
                1. Subscription Terms
              </Typography>
              <Typography variant="body1" paragraph>
                Qixbi services are provided on a subscription basis. By subscribing to our services, you agree to pay the specified fees according to the selected subscription plan. All subscriptions are billed on a monthly basis.
              </Typography>
              <Typography variant="body1" paragraph>
                Free trials are available for evaluation purposes. At the end of the trial period, you will be billed according to the selected plan unless you cancel before the trial ends. No refunds are provided for subscription cancellations.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                2. Service Modifications
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to modify, suspend, or discontinue any part of our services with or without notice. We may also impose limits on certain features or restrict access to parts or all of the service without notice or liability.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                3. Account Security
              </Typography>
              <Typography variant="body1" paragraph>
                You are responsible for maintaining the security of your account credentials and for any activities that occur under your account. Notify us immediately of any unauthorized use of your account or other security breaches.
              </Typography>
            </Box>
          </Stack>
        </TabPanel>

        <TabPanel value={value} index={1}>
          <Stack spacing={4}>
            <Box>
              <Typography variant="h5" gutterBottom>
                1. Data Collection
              </Typography>
              <Typography variant="body1" paragraph>
                Qixbi is committed to minimal data collection. We do not collect user data beyond what is necessary for service operation. We do not use cookies or tracking technologies, and we do not share any data with third parties.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                2. Data Security
              </Typography>
              <Typography variant="body1" paragraph>
                We implement industry-standard security measures to protect any data processed through our services. This includes encryption, secure data storage, and regular security audits.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                3. Your Rights
              </Typography>
              <Typography variant="body1" paragraph>
                You have the right to access, modify, or delete any data associated with your account. Contact our support team for assistance with data-related requests.
              </Typography>
            </Box>
          </Stack>
        </TabPanel>

        <TabPanel value={value} index={2}>
          <Stack spacing={4}>
            <Box>
              <Typography variant="h5" gutterBottom>
                1. Acceptable Use
              </Typography>
              <Typography variant="body1" paragraph>
                You agree not to use our services for any unlawful purposes or in any way that could damage, disable, overburden, or impair our servers or networks. Any attempt to gain unauthorized access to our systems or other accounts is strictly prohibited.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                2. API Usage
              </Typography>
              <Typography variant="body1" paragraph>
                API access is subject to rate limiting and fair usage policies. We reserve the right to modify these limits to ensure service stability for all users.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                3. Content Guidelines
              </Typography>
              <Typography variant="body1" paragraph>
                Users are responsible for all content processed through our services. Content must not violate any applicable laws or intellectual property rights.
              </Typography>
            </Box>
          </Stack>
        </TabPanel>

        <TabPanel value={value} index={3}>
          <Stack spacing={4}>
            <Box>
              <Typography variant="h5" gutterBottom>
                1. Software License
              </Typography>
              <Typography variant="body1" paragraph>
                Our software is licensed, not sold. The license grants you the right to use the software according to the terms of your subscription plan, subject to all the terms and conditions of this agreement.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                2. Intellectual Property
              </Typography>
              <Typography variant="body1" paragraph>
                All intellectual property rights in the software and service remain the property of Qixbi. Users retain all rights to their own code and data processed through our services.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom>
                3. Updates and Support
              </Typography>
              <Typography variant="body1" paragraph>
                We provide regular software updates and technical support according to your subscription plan. While we strive for maximum uptime, we do not guarantee uninterrupted service.
              </Typography>
            </Box>
          </Stack>
        </TabPanel>

        <Box sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" align="center">
            Last updated: {new Date().toLocaleDateString()}. For any questions regarding our legal policies,
            please contact <Link href="mailto:legal@qixbi.com" color="primary">legal@qixbi.com</Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LegalPage;